import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import {
  UserOrgInfo,
  UserOrgInfoDTO,
  UserOrgInfoInput,
  DashboardCollaborator,
  DashboardManager,
  DashboardHR,
  KpiEvaluationLineDetail,
  KpiEvaluationPeriodDetail,
  CreateLineInput,
  RegisterAchievementInput,
  KPIAprobationResponse,
  FileDownloadResponse,
  CodeCatalog,
  CatalogType,
  UpsertCodeCatalogInput,
  KpiEvidence,
  AttachEvidenceInput,
  CompensationUploadInput,
  CompensationUploadResult,
  CompensationAnalysis,
  KpiPayoutCurve,
  UpsertKpiPayoutCurveInput,
  KpiBulkUploadInput,
  KpiBulkUploadResult,
  KpiPeriodConfig,
  UpsertKpiPeriodConfigInput
} from '../models/kpi.model';

const LINE_FIELDS = `
  id
  headerId
  collaboratorId
  name
  type
  description
  assignedWeight
  goal
  achievedValue
  computedContribution
  achievementPercentage
  status
  collaboratorComment
  managerRejectionComment
  hrRejectionComment
  createdAt
  updatedAt
  collaboratorName
  period
  hasEvidence
  evidenceType
  evidenceFileName
  usesPayoutCurve
  payoutCurveId
  payoutCurveName
  appliedPayoutPercentage
  usesTolerance
  draftAchievedValue
  draftComment
  draftSavedAt
  pristine
`;

@Injectable({ providedIn: 'root' })
export class KpiService {

  constructor(private readonly apollo: Apollo) {}

  // ============ ORGANIZACIÓN DE USUARIOS ============

  getUsersByRoleName(roleName: string): Observable<UserOrgInfoDTO[]> {
    return this.apollo.query<{ getUsersByRoleName: UserOrgInfoDTO[] }>({
      query: gql`
        query GetUsersByRoleName($roleName: String!) {
          getUsersByRoleName(roleName: $roleName) {
            id
            fullName: nombreCompleto
          }
        }
      `,
      variables: { roleName },
      fetchPolicy: 'network-only'
    }).pipe(map(result => result.data?.getUsersByRoleName ?? []));
  }

  getOrgInfo(id: string): Observable<UserOrgInfoDTO> {
    return this.apollo.query<{ getOrgInfo: UserOrgInfoDTO }>({
      query: gql`
        query GetOrgInfo($id: ID!) {
          getOrgInfo(id: $id) {
            id userName fullName: nombreCompleto email position department departmentDescription
            geographicArea costCenter costCenterDescription immediateManagerId immediateManagerName
            hireDate departureDate activeInPayroll kpiManagedByHr lastName motherLastName
            firstNames gender salesChannel terminationReason variablePayAmount baseSalary responsibilityBonus
          }
        }
      `,
      variables: { id }
    }).pipe(map(result => result.data.getOrgInfo));
  }

  getEquipoDeJefe(managerId: string): Observable<UserOrgInfoDTO[]> {
    return this.apollo.query<{ getEquipoDeJefe: UserOrgInfoDTO[] }>({
      query: gql`
        query GetEquipoDeJefe($managerId: ID!) {
          getEquipoDeJefe(managerId: $managerId) {
            id userName fullName: nombreCompleto email position department departmentDescription
            geographicArea costCenter costCenterDescription immediateManagerId immediateManagerName
            kpiApproverId kpiApproverName
            hireDate departureDate activeInPayroll kpiManagedByHr lastName motherLastName
            firstNames gender salesChannel terminationReason variablePayAmount
          }
        }
      `,
      variables: { managerId },
      fetchPolicy: 'network-only'
    }).pipe(map(result => result.data?.getEquipoDeJefe ?? []));
  }

  getJefesGestionadosPorTH(): Observable<UserOrgInfoDTO[]> {
    return this.apollo.query<{ getJefesGestionadosPorTH: UserOrgInfoDTO[] }>({
      query: gql`
        query GetJefesGestionadosPorTH {
          getJefesGestionadosPorTH {
            id userName fullName: nombreCompleto email position department departmentDescription
            geographicArea costCenter costCenterDescription immediateManagerId immediateManagerName
            kpiApproverId kpiApproverName
            hireDate departureDate activeInPayroll kpiManagedByHr lastName motherLastName
            firstNames gender salesChannel terminationReason variablePayAmount
          }
        }
      `,
      fetchPolicy: 'network-only'
    }).pipe(map(result => result.data?.getJefesGestionadosPorTH ?? []));
  }

  getUsersByDepartamento(departamento: string): Observable<UserOrgInfoDTO[]> {
    return this.apollo.query<{ getUsersByDepartamento: UserOrgInfoDTO[] }>({
      query: gql`
        query GetUsersByDepartamento($departamento: String!) {
          getUsersByDepartamento(departamento: $departamento) {
            id userName fullName: nombreCompleto email position department departmentDescription
            geographicArea costCenter costCenterDescription immediateManagerId immediateManagerName
            hireDate departureDate activeInPayroll lastName motherLastName
            firstNames gender salesChannel terminationReason variablePayAmount
          }
        }
      `,
      variables: { departamento }
    }).pipe(map(result => result.data.getUsersByDepartamento));
  }

  getUsersByArea(area: string): Observable<UserOrgInfoDTO[]> {
    return this.apollo.query<{ getUsersByArea: UserOrgInfoDTO[] }>({
      query: gql`
        query GetUsersByArea($area: String!) {
          getUsersByArea(area: $area) {
            id userName fullName: nombreCompleto email position department departmentDescription
            geographicArea costCenter costCenterDescription immediateManagerId immediateManagerName
            hireDate departureDate activeInPayroll lastName motherLastName
            firstNames gender salesChannel terminationReason variablePayAmount
          }
        }
      `,
      variables: { area }
    }).pipe(map(result => result.data.getUsersByArea));
  }

  validarJefeInmediato(userId: string, managerId: string): Observable<boolean> {
    return this.apollo.query<{ validarJefeInmediato: boolean }>({
      query: gql`
        query ValidarJefeInmediato($userId: ID!, $managerId: ID!) {
          validarJefeInmediato(userId: $userId, managerId: $managerId)
        }
      `,
      variables: { userId, managerId }
    }).pipe(map(result => result.data.validarJefeInmediato));
  }

  // ============ MÓDULO 6 — DASHBOARD ============

  getDashboardCollaborator(collaboratorId: string, period: string): Observable<DashboardCollaborator> {
    return this.apollo.query<{ getDashboardCollaborator: DashboardCollaborator }>({
      query: gql`
        query GetDashboardCollaborator($collaboratorId: ID!, $period: String!) {
          getDashboardCollaborator(collaboratorId: $collaboratorId, period: $period) {
            period {
              id period status totalWeight weightedResult variableAmount bonusAmount
              lines { id name type assignedWeight goal achievedValue computedContribution status }
            }
            trend { period weightedResult }
          }
        }
      `,
      variables: { collaboratorId, period },
      fetchPolicy: 'network-only'
    }).pipe(map(result => result.data.getDashboardCollaborator));
  }

  getDashboardManager(managerId: string, period: string): Observable<DashboardManager> {
    return this.apollo.query<{ getDashboardManager: DashboardManager }>({
      query: gql`
        query GetDashboardManager($managerId: ID!, $period: String!) {
          getDashboardManager(managerId: $managerId, period: $period) {
            evaluatedCount approvedByManagerCount pendingCount avgWeightedResult
            team {
              id collaboratorId collaboratorName period status totalWeight weightedResult
              department departmentDescription costCenter costCenterDescription
              dni email immediateManagerName
              lines { id status }
            }
            trend { period weightedResult }
          }
        }
      `,
      variables: { managerId, period },
      fetchPolicy: 'network-only'
    }).pipe(map(result => result.data.getDashboardManager));
  }

  getDashboardHR(period: string, department?: string, area?: string, managerId?: string, trendMonths?: number): Observable<DashboardHR> {
    return this.apollo.query<{ getDashboardHR: DashboardHR }>({
      query: gql`
        query GetDashboardHR($period: String!, $department: String, $area: String, $managerId: ID, $trendMonths: Int) {
          getDashboardHR(period: $period, department: $department, area: $area, managerId: $managerId, trendMonths: $trendMonths) {
            evaluatedCount approvedByHrCount pendingCount avgWeightedResult totalBonusAmount
            collaborators {
              id collaboratorId collaboratorName managerId period status totalWeight weightedResult
              variableAmount bonusAmount department departmentDescription
              costCenter costCenterDescription position
              dni email immediateManagerName baseSalary responsibilityBonus
              lines {
                id name type description assignedWeight goal achievedValue computedContribution
                achievementPercentage usesTolerance usesPayoutCurve payoutCurveName
                managerRejectionComment hrRejectionComment
                status createdAt managerApprovedAt
              }
            }
            trend { period weightedResult }
          }
        }
      `,
      variables: {
        period, department: department || null, area: area || null,
        managerId: managerId || null, trendMonths: trendMonths || null
      },
      fetchPolicy: 'network-only'
    }).pipe(map(result => result.data.getDashboardHR));
  }

  // ============ KPI EVALUATION — DEFINICIÓN (JEFE) ============

  addLine(input: CreateLineInput): Observable<KpiEvaluationLineDetail> {
    return this.apollo.mutate<{ addLine: KpiEvaluationLineDetail }>({
      mutation: gql`
        mutation AddLine($input: CreateLineInput!) {
          addLine(input: $input) { ${LINE_FIELDS} }
        }
      `,
      variables: { input }
    }).pipe(map(result => result.data.addLine));
  }

  updateLine(lineId: string, input: CreateLineInput): Observable<KpiEvaluationLineDetail> {
    return this.apollo.mutate<{ updateLine: KpiEvaluationLineDetail }>({
      mutation: gql`
        mutation UpdateLine($lineId: ID!, $input: CreateLineInput!) {
          updateLine(lineId: $lineId, input: $input) { ${LINE_FIELDS} }
        }
      `,
      variables: { lineId, input }
    }).pipe(map(result => result.data.updateLine));
  }

  deactivateOrDeleteLine(lineId: string, updatedBy: string): Observable<boolean> {
    return this.apollo.mutate<{ deactivateOrDeleteLine: boolean }>({
      mutation: gql`
        mutation DeactivateOrDeleteLine($lineId: ID!, $updatedBy: String!) {
          deactivateOrDeleteLine(lineId: $lineId, updatedBy: $updatedBy)
        }
      `,
      variables: { lineId, updatedBy }
    }).pipe(map(result => result.data.deactivateOrDeleteLine));
  }

  // ============ KPI EVALUATION — REGISTRO (COLABORADOR) ============

  registerAchievement(lineId: string, input: RegisterAchievementInput): Observable<KpiEvaluationLineDetail> {
    return this.apollo.mutate<{ registerAchievement: KpiEvaluationLineDetail }>({
      mutation: gql`
        mutation RegisterAchievement($lineId: ID!, $input: RegisterAchievementInput!) {
          registerAchievement(lineId: $lineId, input: $input) { ${LINE_FIELDS} }
        }
      `,
      variables: { lineId, input }
    }).pipe(map(result => result.data.registerAchievement));
  }

  saveAchievementDrafts(items: { lineId: string; achievedValue: number | null; collaboratorComment: string | null }[]): Observable<any> {
    return this.apollo.mutate<any>({
      mutation: gql`
        mutation SaveAchievementDrafts($input: SaveAchievementDraftsInput!) {
          saveAchievementDrafts(input: $input) {
            id draftAchievedValue draftComment draftSavedAt
          }
        }
      `,
      variables: { input: { items } }
    }).pipe(map(result => result.data?.saveAchievementDrafts));
  }

  // ============ KPI EVALUATION — APROBACIÓN ============

  approveByManager(lineId: string, approverId: string, observation?: string): Observable<KPIAprobationResponse> {
    return this.apollo.mutate<{ approveByManager: KPIAprobationResponse }>({
      mutation: gql`
        mutation ApproveByManager($lineId: ID!, $approverId: ID!, $observation: String) {
          approveByManager(lineId: $lineId, approverId: $approverId, observation: $observation) {
            id status approvalDate
          }
        }
      `,
      variables: { lineId, approverId, observation: observation || null }
    }).pipe(map((result: any) => result.data.approveByManager));
  }

  rejectByManager(lineId: string, approverId: string, reason: string): Observable<KPIAprobationResponse> {
    return this.apollo.mutate<{ rejectByManager: KPIAprobationResponse }>({
      mutation: gql`
        mutation RejectByManager($lineId: ID!, $approverId: ID!, $reason: String!) {
          rejectByManager(lineId: $lineId, approverId: $approverId, reason: $reason) {
            id status approvalDate
          }
        }
      `,
      variables: { lineId, approverId, reason }
    }).pipe(map((result: any) => result.data.rejectByManager));
  }

  approveByHr(lineId: string, approverId: string, observation?: string): Observable<KPIAprobationResponse> {
    return this.apollo.mutate<{ approveByHr: KPIAprobationResponse }>({
      mutation: gql`
        mutation ApproveByHr($lineId: ID!, $approverId: ID!, $observation: String) {
          approveByHr(lineId: $lineId, approverId: $approverId, observation: $observation) {
            id status approvalDate
          }
        }
      `,
      variables: { lineId, approverId, observation: observation || null }
    }).pipe(map((result: any) => result.data.approveByHr));
  }

  rejectByHr(lineId: string, approverId: string, reason: string): Observable<KPIAprobationResponse> {
    return this.apollo.mutate<{ rejectByHr: KPIAprobationResponse }>({
      mutation: gql`
        mutation RejectByHr($lineId: ID!, $approverId: ID!, $reason: String!) {
          rejectByHr(lineId: $lineId, approverId: $approverId, reason: $reason) {
            id status approvalDate
          }
        }
      `,
      variables: { lineId, approverId, reason }
    }).pipe(map((result: any) => result.data.rejectByHr));
  }

  // ============ KPI EVALUATION — CONSULTAS ============

  getPeriodWithLines(collaboratorId: string, period: string): Observable<KpiEvaluationPeriodDetail> {
    return this.apollo.query<{ getPeriodWithLines: KpiEvaluationPeriodDetail }>({
      query: gql`
        query GetPeriodWithLines($collaboratorId: ID!, $period: String!) {
          getPeriodWithLines(collaboratorId: $collaboratorId, period: $period) {
            id collaboratorId collaboratorName managerId period status
            totalWeight weightedResult variableAmount bonusAmount createdAt
            sentToManagerAt approvedByManagerAt approvedByHrAt
            lines { ${LINE_FIELDS} }
          }
        }
      `,
      variables: { collaboratorId, period },
      fetchPolicy: 'network-only'
    }).pipe(map(result => result.data.getPeriodWithLines));
  }

  getKpisByEquipo(managerId: string, period: string, status?: string): Observable<KpiEvaluationPeriodDetail[]> {
    return this.apollo.query<{ getKpisByEquipo: KpiEvaluationPeriodDetail[] }>({
      query: gql`
        query GetKpisByEquipo($managerId: ID!, $period: String!, $status: String) {
          getKpisByEquipo(managerId: $managerId, period: $period, status: $status) {
            id collaboratorId collaboratorName managerId period status
            totalWeight weightedResult variableAmount bonusAmount
            sentToManagerAt approvedByManagerAt approvedByHrAt
            department departmentDescription costCenter costCenterDescription
            dni email immediateManagerName
            lines {
              id headerId collaboratorId name type description assignedWeight goal
              achievedValue computedContribution
              achievementPercentage
              status collaboratorComment managerRejectionComment hrRejectionComment createdAt
              hasEvidence evidenceType evidenceFileName
              usesPayoutCurve payoutCurveId payoutCurveName appliedPayoutPercentage
              usesTolerance pristine
            }
          }
        }
      `,
      variables: { managerId, period, status: status || null },
      fetchPolicy: 'network-only'
    }).pipe(map(result => result.data?.getKpisByEquipo ?? []));
  }

  getKpisForJefesGestionadosPorTH(period: string): Observable<KpiEvaluationPeriodDetail[]> {
    return this.apollo.query<{ getKpisForJefesGestionadosPorTH: KpiEvaluationPeriodDetail[] }>({
      query: gql`
        query GetKpisForJefesGestionadosPorTH($period: String!) {
          getKpisForJefesGestionadosPorTH(period: $period) {
            id collaboratorId collaboratorName managerId period status
            totalWeight weightedResult variableAmount bonusAmount
            sentToManagerAt approvedByManagerAt approvedByHrAt
            department departmentDescription costCenter costCenterDescription
            dni email immediateManagerName
            lines {
              id headerId collaboratorId name type description assignedWeight goal
              achievedValue computedContribution
              achievementPercentage
              status collaboratorComment managerRejectionComment hrRejectionComment createdAt
              hasEvidence evidenceType evidenceFileName
              usesPayoutCurve payoutCurveId payoutCurveName appliedPayoutPercentage
              usesTolerance pristine
            }
          }
        }
      `,
      variables: { period },
      fetchPolicy: 'network-only'
    }).pipe(map(result => result.data?.getKpisForJefesGestionadosPorTH ?? []));
  }

  getKpisForThValidation(period: string): Observable<KpiEvaluationPeriodDetail[]> {
    return this.apollo.query<{ getKpisForThValidation: KpiEvaluationPeriodDetail[] }>({
      query: gql`
        query GetKpisForThValidation($period: String!) {
          getKpisForThValidation(period: $period) {
            id collaboratorId collaboratorName managerId period status
            totalWeight weightedResult variableAmount bonusAmount
            sentToManagerAt approvedByManagerAt approvedByHrAt
            department departmentDescription costCenter costCenterDescription
            dni email immediateManagerName
            lines {
              id headerId collaboratorId name type description assignedWeight goal
              achievedValue computedContribution
              achievementPercentage
              status collaboratorComment managerRejectionComment hrRejectionComment createdAt
              hasEvidence evidenceType evidenceFileName
              usesPayoutCurve payoutCurveId payoutCurveName appliedPayoutPercentage
              usesTolerance
            }
          }
        }
      `,
      variables: { period },
      fetchPolicy: 'network-only'
    }).pipe(map(result => result.data?.getKpisForThValidation ?? []));
  }

  getKpisForPayrollDownload(period: string): Observable<KpiEvaluationPeriodDetail[]> {
    return this.apollo.query<{ getKpisForPayrollDownload: KpiEvaluationPeriodDetail[] }>({
      query: gql`
        query GetKpisForPayrollDownload($period: String!) {
          getKpisForPayrollDownload(period: $period) {
            id
            collaboratorId
            collaboratorName
            period
            status
            totalWeight
            weightedResult
            downloadedAt
            variableAmount
            bonusAmount
            lines {
              id
              name
              type
              assignedWeight
              goal
              achievedValue
              computedContribution
              status
            }
          }
        }
      `,
      variables: { period },
      fetchPolicy: 'network-only'
    }).pipe(map(result => result.data?.getKpisForPayrollDownload ?? []));
  }

  // ============ KPI EVALUATION — DESCARGA NÓMINA ============
  // markAsDownloaded ya NO existe — el backend la ejecuta internamente.

  downloadAsExcel(period: string, payrollUserId?: string): Observable<FileDownloadResponse> {
    return this.apollo.mutate<{ downloadAsExcel: FileDownloadResponse }>({
      mutation: gql`
        mutation DownloadAsExcel($period: String!, $payrollUserId: String) {
          downloadAsExcel(period: $period, payrollUserId: $payrollUserId) {
            fileName contentType fileContent success message
          }
        }
      `,
      variables: { period, payrollUserId }
    }).pipe(
      map(result => result.data.downloadAsExcel),
      tap((response: FileDownloadResponse) => {
        if (response?.success) {
          this.triggerBase64Download(response.fileContent, response.fileName, response.contentType);
        }
      })
    );
  }

  downloadAsCsv(period: string, payrollUserId?: string): Observable<FileDownloadResponse> {
    return this.apollo.mutate<{ downloadAsCsv: FileDownloadResponse }>({
      mutation: gql`
        mutation DownloadAsCsv($period: String!, $payrollUserId: String) {
          downloadAsCsv(period: $period, payrollUserId: $payrollUserId) {
            fileName contentType fileContent success message
          }
        }
      `,
      variables: { period, payrollUserId }
    }).pipe(
      map(result => result.data.downloadAsCsv),
      tap((response: FileDownloadResponse) => {
        if (response?.success) {
          this.triggerBase64Download(response.fileContent, response.fileName, response.contentType);
        }
      })
    );
  }

  // ============ USER ORG MANAGEMENT ============

  getAllUsersOrgInfo(): Observable<UserOrgInfoDTO[]> {
    return this.apollo.query<{ getAllUsersOrgInfo: UserOrgInfoDTO[] }>({
      query: gql`
        query GetAllUsersOrgInfo {
          getAllUsersOrgInfo {
            id userName fullName: nombreCompleto email position department departmentDescription
            geographicArea costCenter costCenterDescription immediateManagerId immediateManagerName
            hireDate departureDate activeInPayroll kpiManagedByHr lastName motherLastName
            firstNames gender salesChannel terminationReason variablePayAmount baseSalary responsibilityBonus dni
          }
        }
      `,
      fetchPolicy: 'network-only'
    }).pipe(map(result => result.data.getAllUsersOrgInfo));
  }

  updateUserOrgInfo(id: string, input: UserOrgInfoInput): Observable<UserOrgInfoDTO> {
    return this.apollo.mutate<{ updateUserOrgInfo: UserOrgInfoDTO }>({
      mutation: gql`
        mutation UpdateUserOrgInfo($id: ID!, $input: UserOrgInfoInput!) {
          updateUserOrgInfo(id: $id, input: $input) {
            id userName fullName: nombreCompleto email position department departmentDescription
            geographicArea costCenter costCenterDescription immediateManagerId immediateManagerName
            hireDate departureDate activeInPayroll kpiManagedByHr lastName motherLastName
            firstNames gender salesChannel terminationReason variablePayAmount baseSalary responsibilityBonus
          }
        }
      `,
      variables: { id, input }
    }).pipe(map((result: any) => result.data.updateUserOrgInfo));
  }

  // ============ CODE CATALOG ============

  getCodeCatalogByType(catalogType: CatalogType): Observable<CodeCatalog[]> {
    return this.apollo.query<{ getCodeCatalogByType: CodeCatalog[] }>({
      query: gql`
        query GetCodeCatalogByType($catalogType: String!) {
          getCodeCatalogByType(catalogType: $catalogType) {
            id catalogType code description active departmentPrefixWarning
          }
        }
      `,
      variables: { catalogType },
      fetchPolicy: 'network-only'
    }).pipe(map(result => result.data?.getCodeCatalogByType ?? []));
  }

  upsertCodeCatalog(input: UpsertCodeCatalogInput): Observable<CodeCatalog> {
    return this.apollo.mutate<{ upsertCodeCatalog: CodeCatalog }>({
      mutation: gql`
        mutation UpsertCodeCatalog($input: UpsertCodeCatalogInput!) {
          upsertCodeCatalog(input: $input) {
            id catalogType code description active departmentPrefixWarning
          }
        }
      `,
      variables: { input }
    }).pipe(map((result: any) => result.data.upsertCodeCatalog));
  }

  deactivateCodeCatalog(id: string): Observable<boolean> {
    return this.apollo.mutate<{ deactivateCodeCatalog: boolean }>({
      mutation: gql`
        mutation DeactivateCodeCatalog($id: ID!) {
          deactivateCodeCatalog(id: $id)
        }
      `,
      variables: { id }
    }).pipe(map((result: any) => result.data.deactivateCodeCatalog));
  }

  // ============ EVIDENCE ============

  getEvidence(lineId: string): Observable<KpiEvidence | null> {
    return this.apollo.query<{ getEvidence: KpiEvidence | null }>({
      query: gql`
        query GetEvidence($lineId: ID!) {
          getEvidence(lineId: $lineId) {
            lineId evidenceType fileName contentType content url uploadedAt
          }
        }
      `,
      variables: { lineId },
      fetchPolicy: 'network-only'
    }).pipe(map(result => result.data?.getEvidence ?? null));
  }

  attachEvidence(lineId: string, input: AttachEvidenceInput): Observable<KpiEvidence> {
    return this.apollo.mutate<{ attachEvidence: KpiEvidence }>({
      mutation: gql`
        mutation AttachEvidence($lineId: ID!, $input: AttachEvidenceInput!) {
          attachEvidence(lineId: $lineId, input: $input) {
            lineId evidenceType fileName contentType content url uploadedAt
          }
        }
      `,
      variables: { lineId, input }
    }).pipe(map((result: any) => result.data.attachEvidence));
  }

  removeEvidence(lineId: string): Observable<boolean> {
    return this.apollo.mutate<{ removeEvidence: boolean }>({
      mutation: gql`
        mutation RemoveEvidence($lineId: ID!) {
          removeEvidence(lineId: $lineId)
        }
      `,
      variables: { lineId }
    }).pipe(map((result: any) => result.data.removeEvidence));
  }

  // ============ COMPENSATION ============

  bulkUploadCompensation(input: CompensationUploadInput): Observable<CompensationUploadResult> {
    return this.apollo.mutate<{ bulkUploadCompensation: CompensationUploadResult }>({
      mutation: gql`
        mutation BulkUploadCompensation($input: CompensationUploadInput!) {
          bulkUploadCompensation(input: $input) {
            uploadLogId
            totalRows
            successCount
            failedCount
            failedRows { ci reason }
          }
        }
      `,
      variables: { input }
    }).pipe(map((result: any) => result.data.bulkUploadCompensation));
  }

  getCompensationAnalysis(period: string): Observable<CompensationAnalysis[]> {
    return this.apollo.query<{ getCompensationAnalysis: CompensationAnalysis[] }>({
      query: gql`
        query GetCompensationAnalysis($period: String!) {
          getCompensationAnalysis(period: $period) {
            collaboratorId collaboratorName dni department departmentDescription
            costCenter costCenterDescription position immediateManagerId immediateManagerName
            baseSalary responsibilityBonus totalFixedSalary variableAmount
            currentPeriod commissionCurrent
            previousPeriod commissionPrevious
            differenceAmount differencePercentage
            vsMaxAmount vsMaxPercentage
          }
        }
      `,
      variables: { period },
      fetchPolicy: 'network-only'
    }).pipe(map(result => result.data?.getCompensationAnalysis ?? []));
  }

  // ============ PAYOUT CURVES ============

  getActivePayoutCurves(): Observable<KpiPayoutCurve[]> {
    return this.apollo.query<{ getActivePayoutCurves: KpiPayoutCurve[] }>({
      query: gql`
        query GetActivePayoutCurves {
          getActivePayoutCurves {
            id code name description active
            points { thresholdPercentage payoutPercentage }
          }
        }
      `,
      fetchPolicy: 'network-only'
    }).pipe(map(result => result.data?.getActivePayoutCurves ?? []));
  }

  upsertPayoutCurve(input: UpsertKpiPayoutCurveInput): Observable<KpiPayoutCurve> {
    return this.apollo.mutate<{ upsertPayoutCurve: KpiPayoutCurve }>({
      mutation: gql`
        mutation UpsertPayoutCurve($input: UpsertKpiPayoutCurveInput!) {
          upsertPayoutCurve(input: $input) {
            id code name description active
            points { thresholdPercentage payoutPercentage }
          }
        }
      `,
      variables: { input }
    }).pipe(map((result: any) => result.data.upsertPayoutCurve));
  }

  deactivatePayoutCurve(id: string): Observable<boolean> {
    return this.apollo.mutate<{ deactivatePayoutCurve: boolean }>({
      mutation: gql`
        mutation DeactivatePayoutCurve($id: ID!) {
          deactivatePayoutCurve(id: $id)
        }
      `,
      variables: { id }
    }).pipe(map((result: any) => result.data.deactivatePayoutCurve));
  }

  // ============ KPI EVALUATION — PLANTILLA Y CARGA MASIVA (JEFE) ============

  downloadKpiBulkUploadTemplate(managerId: string, period: string, includeJefesTH = false, sourcePeriod?: string): Observable<FileDownloadResponse> {
    return this.apollo.mutate<{ downloadKpiBulkUploadTemplate: FileDownloadResponse }>({
      mutation: gql`
        mutation DownloadKpiBulkUploadTemplate($managerId: ID!, $period: String!, $includeJefesTH: Boolean, $sourcePeriod: String) {
          downloadKpiBulkUploadTemplate(managerId: $managerId, period: $period, includeJefesTH: $includeJefesTH, sourcePeriod: $sourcePeriod) {
            fileName contentType fileContent success message
          }
        }
      `,
      variables: { managerId, period, includeJefesTH, sourcePeriod }
    }).pipe(
      map(result => result.data.downloadKpiBulkUploadTemplate),
      tap((response: FileDownloadResponse) => {
        if (response?.success) {
          this.triggerBase64Download(response.fileContent, response.fileName, response.contentType);
        }
      })
    );
  }

  bulkUploadKpiLines(input: KpiBulkUploadInput): Observable<KpiBulkUploadResult> {
    return this.apollo.mutate<{ bulkUploadKpiLines: KpiBulkUploadResult }>({
      mutation: gql`
        mutation BulkUploadKpiLines($input: KpiBulkUploadInput!) {
          bulkUploadKpiLines(input: $input) {
            totalRows
            successCount
            failedCount
            failedRows { row dni kpiName reason }
          }
        }
      `,
      variables: { input }
    }).pipe(map((result: any) => result.data.bulkUploadKpiLines));
  }

  // ============ KPI CÉLULA — DELEGACIÓN DE APROBACIÓN ============

  getColaboradoresAsignadosComoAprobador(approverId: string): Observable<UserOrgInfoDTO[]> {
    return this.apollo.query<{ getColaboradoresAsignadosComoAprobador: UserOrgInfoDTO[] }>({
      query: gql`
        query GetColaboradoresAsignadosComoAprobador($approverId: ID!) {
          getColaboradoresAsignadosComoAprobador(approverId: $approverId) {
            id userName fullName: nombreCompleto email position department departmentDescription
            geographicArea costCenter costCenterDescription immediateManagerId immediateManagerName
            kpiApproverId kpiApproverName
            hireDate departureDate activeInPayroll kpiManagedByHr lastName motherLastName
            firstNames gender salesChannel terminationReason variablePayAmount
          }
        }
      `,
      variables: { approverId },
      fetchPolicy: 'network-only'
    }).pipe(map(result => result.data?.getColaboradoresAsignadosComoAprobador ?? []));
  }

  assignKpiApprover(collaboratorId: string, approverId: string | null, requestedByManagerId: string): Observable<UserOrgInfoDTO> {
    return this.apollo.mutate<{ assignKpiApprover: UserOrgInfoDTO }>({
      mutation: gql`
        mutation AssignKpiApprover($collaboratorId: ID!, $approverId: ID, $requestedByManagerId: ID!) {
          assignKpiApprover(collaboratorId: $collaboratorId, approverId: $approverId, requestedByManagerId: $requestedByManagerId) {
            id kpiApproverId kpiApproverName
          }
        }
      `,
      variables: { collaboratorId, approverId, requestedByManagerId }
    }).pipe(map((result: any) => result.data.assignKpiApprover));
  }

  getKpisForColaboradoresAsignados(approverId: string, period: string): Observable<KpiEvaluationPeriodDetail[]> {
    return this.apollo.query<{ getKpisForColaboradoresAsignados: KpiEvaluationPeriodDetail[] }>({
      query: gql`
        query GetKpisForColaboradoresAsignados($approverId: String!, $period: String!) {
          getKpisForColaboradoresAsignados(approverId: $approverId, period: $period) {
            id collaboratorId collaboratorName managerId period status
            totalWeight weightedResult variableAmount bonusAmount
            sentToManagerAt approvedByManagerAt approvedByHrAt
            department departmentDescription costCenter costCenterDescription
            dni email immediateManagerName
            lines {
              id headerId collaboratorId name type description assignedWeight goal
              achievedValue computedContribution
              achievementPercentage
              status collaboratorComment managerRejectionComment hrRejectionComment createdAt
              hasEvidence evidenceType evidenceFileName
              usesPayoutCurve payoutCurveId payoutCurveName appliedPayoutPercentage
              usesTolerance
            }
          }
        }
      `,
      variables: { approverId, period },
      fetchPolicy: 'network-only'
    }).pipe(map(result => result.data?.getKpisForColaboradoresAsignados ?? []));
  }

  // ============ KPI PERIOD CONFIG (LÍDER DH) ============

  getKpiPeriods(): Observable<KpiPeriodConfig[]> {
    return this.apollo.query<{ getKpiPeriods: KpiPeriodConfig[] }>({
      query: gql`
        query GetKpiPeriods {
          getKpiPeriods { id period enabled enabledAt enabledBy disabledAt disabledBy notes }
        }
      `,
      fetchPolicy: 'network-only'
    }).pipe(map(result => result.data?.getKpiPeriods ?? []));
  }

  getEnabledKpiPeriods(): Observable<KpiPeriodConfig[]> {
    return this.apollo.query<{ getEnabledKpiPeriods: KpiPeriodConfig[] }>({
      query: gql`
        query GetEnabledKpiPeriods {
          getEnabledKpiPeriods { id period enabled enabledAt enabledBy disabledAt disabledBy notes }
        }
      `,
      fetchPolicy: 'network-only'
    }).pipe(map(result => result.data?.getEnabledKpiPeriods ?? []));
  }

  upsertKpiPeriodConfig(input: UpsertKpiPeriodConfigInput): Observable<KpiPeriodConfig> {
    return this.apollo.mutate<{ upsertKpiPeriodConfig: KpiPeriodConfig }>({
      mutation: gql`
        mutation UpsertKpiPeriodConfig($input: UpsertKpiPeriodConfigInput!) {
          upsertKpiPeriodConfig(input: $input) {
            id period enabled enabledAt enabledBy disabledAt disabledBy notes
          }
        }
      `,
      variables: { input }
    }).pipe(map((result: any) => result.data.upsertKpiPeriodConfig));
  }

  // ============ PRIVATE HELPERS ============

  private triggerBase64Download(base64Content: string, fileName: string, contentType: string): void {
    const binary = atob(base64Content);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: contentType });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href     = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  }
}
